import { useMemo } from 'react';

import { EmptyState } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconSearch } from '@components/icons/search';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Table, TableProps, TableView } from '@components/table';
import { useTableStore } from '@components/table/table-store';
import { useEvent } from '@hooks/use-event';
import { pxToRem } from '@utils/converters';

import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import { useIsOutOfCredits } from 'api/authentication/hooks';
import {
  useCreateAttribute,
  useRemoveAttribute,
  useUpdateJourneyUserSettings,
} from 'api/journeys/hooks';
import { JourneyAttributeData, JourneyUserSettings } from 'api/journeys/requests';
import { useJourneyTravellersRaw } from 'api/traveller-journeys/hooks';
import { JourneyType, TravellerJourneyListItem } from 'api/types';
import { useUsers } from 'api/users/hooks';
import { useAddAttributeSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/form';

import {
  createBaseColumns,
  makeAttributeId,
  useActionColumns,
  useAttributeColumns,
  useDebouncedFieldsFetch,
} from './columns';
import { useExtraStateStore } from './extra-state-store';
import { t } from './locale';
import { useTravellersColumnControlStore } from './travellers-column-control-store';
import { useFilterConfig } from './use-filter-config';

import { useCompanyTravellerToJourneySidebar } from '../../travellers/company-traveller-to-journey';
import { CustomColumnControl } from '../custom-column-control';
import { TravellerOptionsMenu } from '../traveller-options-menu';

type JourneyTravellerListProps = Pick<TableProps, 'tableRef'> & {
  journey: JourneyType;
  attributes: Array<AttributeType | TravellerAttributeTemplate>;
  searchStr: string;
  setSelectedTravellers(travellers: Array<TravellerJourneyListItem>): void;
  settings: JourneyUserSettings['settings'];
};

export const TravellerJourneysTable = ({
  journey,
  tableRef,
  attributes,
  searchStr,
  setSelectedTravellers,
  settings,
}: JourneyTravellerListProps) => {
  const travellersQuery = useJourneyTravellersRaw(journey.shortId);
  const usersQuery = useUsers();
  const isOutOfCredits = useIsOutOfCredits();
  const showEditTravellerSidebar = useCompanyTravellerToJourneySidebar();
  const updateJourneyUserSettings = useUpdateJourneyUserSettings(journey.shortId);
  const extraStateStore = useExtraStateStore(settings?.extraState);
  const showAddAttributeSidebar = useAddAttributeSidebar();
  const createAttributeMutation = useCreateAttribute();
  const showDeleteModal = useDeleteModal();
  const removeAttributeMutation = useRemoveAttribute(journey.shortId);
  const handleFieldsNeeded = useDebouncedFieldsFetch(journey.shortId);

  const onPersist = useEvent((newState: TableView) => {
    updateJourneyUserSettings.mutate({
      settings: {
        viewState: newState,
        extraState: extraStateStore.actions.getState(),
      },
    });
  });

  const tableStore = useTableStore({
    selectedView: settings?.viewState,
    onPersist,
  });

  const columnControlStore = useTravellersColumnControlStore(
    tableStore,
    extraStateStore,
    handleFieldsNeeded
  );

  const allowedAttributes = useMemo(() => {
    return attributes.filter((attribute) => {
      return attribute.isActive;
    });
  }, [attributes]);

  const attributeColumns = useAttributeColumns({
    attributes: allowedAttributes,
    usersData: usersQuery.data,
    showEditTravellerSidebar,
  });

  const actionColumns = useActionColumns({
    actionsData: journey.actionsData,
    extraStateStore,
    tableStore,
    onPersist,
    columnControlStore,
  });

  const availableColumns = useMemo(() => {
    return createBaseColumns().concat(attributeColumns).concat(actionColumns);
  }, [attributeColumns, actionColumns]);

  const parsedTravellers = useMemo(() => {
    return (travellersQuery.data ?? []).map((traveller) => {
      const dynamicValues = attributes.reduce((acc, attribute) => {
        const attributeId = makeAttributeId(
          attribute.shortId,
          Boolean(attribute.isTravellerAttribute)
        );
        const attributeArray = attribute.isTravellerAttribute
          ? traveller.companyTravellerAttributes
          : traveller.journeyAttributes;

        const travellerAttribute = attributeArray.find(
          (journeyAttribute) => journeyAttribute.attributeShortId === attribute.shortId
        );

        if (attribute.attributeType === 'multi_select') {
          acc[attributeId] = travellerAttribute?.multiOptionValues;
          return acc;
        }

        let attributeValue = travellerAttribute?.value;
        if (!attributeValue) {
          const files = travellerAttribute?.files;
          if (files?.length) {
            attributeValue = files;
          }
        }

        acc[attributeId] = attributeValue;

        return acc;
      }, {} as Record<string, any>);

      return {
        ...traveller,
        status: isOutOfCredits && traveller.status === 'in_progress' ? 'paused' : traveller.status,
        ...dynamicValues,
        ...traveller.actionsData,
      };
    });
  }, [attributes, travellersQuery.data, isOutOfCredits]);

  const endFixedColumns: TableProps<TravellerJourneyListItem>['endFixedColumns'] = useMemo(() => {
    return [
      {
        id: 'options',
        title: '',
        type: 'custom',
        Cell: TravellerOptionsMenu,
        renderHeaderIcon: () => (
          <IconButton
            style={{ height: pxToRem(40), width: pxToRem(40) }}
            icon={<IconAddPlus />}
            variant="ghost-purple"
            onClick={() =>
              showAddAttributeSidebar({
                isTravellerAttribute: false,
                onCreate: async (data: JourneyAttributeData) => {
                  const response = await createAttributeMutation.mutateAsync({
                    journeyShortId: journey.shortId,
                    data,
                  });
                  columnControlStore.actions.toggleColumn(
                    makeAttributeId(response.shortId, false),
                    true
                  );
                  return response;
                },
              })
            }
          />
        ),
      },
    ];
  }, [
    columnControlStore.actions,
    createAttributeMutation,
    journey.shortId,
    showAddAttributeSidebar,
  ]);

  const actionsData = journey.actionsData!;

  const { isLoading: configLoading, config } = useFilterConfig(
    allowedAttributes,
    extraStateStore,
    tableStore,
    actionsData
  );

  if (travellersQuery.isLoading || usersQuery.isLoading) {
    return <Spinner />;
  }

  if (parsedTravellers.length === 0) {
    return (
      <EmptyState
        icon={<IconSearch size={80} />}
        header={t('emptyHeader')}
        content={t('emptyContent')}
      />
    );
  }

  const additionalHeaderMenuItems = (fieldId: string) => {
    const isJourneyAttribute = fieldId.startsWith('journey-attribute');
    const shortId = fieldId.split('-')[2];
    return isJourneyAttribute ? (
      <Menu.Item
        label={t('core.delete')}
        variant="danger"
        icon={<IconTrash color="red100" />}
        onSelect={() =>
          showDeleteModal({
            title: t('core.delete'),
            description: t('areYouSureRemove'),
            hasUserConfirmation: true,
            buttonText: t('core.delete'),
            buttonIcon: <IconTrash />,
            onConfirm: () => {
              removeAttributeMutation.mutateAsync(shortId);
            },
          })
        }
      />
    ) : null;
  };

  return (
    <Table<TravellerJourneyListItem>
      idColumn="shortId"
      searchStr={searchStr}
      tableRef={tableRef}
      data={parsedTravellers}
      availableColumns={availableColumns}
      endFixedColumns={endFixedColumns}
      store={tableStore}
      onSelectChange={setSelectedTravellers}
      additionalHeaderMenuItems={additionalHeaderMenuItems}
      filterProps={{
        addFilterText: 'Filter',
        countSuffix: 'travellers',
        config,
        isLoading: configLoading,
      }}
      columnControl={
        <CustomColumnControl
          availableColumns={availableColumns}
          tableStore={tableStore}
          extraStateStore={extraStateStore}
          actionsData={actionsData}
          columnControlStore={columnControlStore}
        />
      }
    />
  );
};
